/**
 * Scheda giocatore — Voti (V39).
 *
 * Il Mister legge e modifica i soli voti delle sue squadre-stagione; l'Admin
 * li legge tutti, senza modificarli. Senza V27, un voto di partita si assegna da qui.
 * In cima le medie; sotto, partite e allenamenti in due card, con il voto
 * colorato per fascia, dieci per pagina.
 */

import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OctagonXIcon, PencilIcon } from 'lucide-react';
import { votoGiocatoreSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { ComandoIcona, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { usePaginazione } from '../comuni/Paginazione.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { voce } from '../navigazione/voci.js';
import { coloreVoto } from './colori-stato.js';
import {
  type PartitaSenzaVoto,
  type VotiGiocatore as Dati,
  type VotoRiga,
  VALORI_SCALA,
} from './elenchi.js';

export function VotiGiocatore() {
  const { id } = useParams<{ id: string }>();
  const { dati, errore, stato, ricarica } = useDati<Dati>(`/giocatori/${id}/voti`);

  if (stato === 403 || stato === 404) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  const partite = dati.voti.filter((v) => v.tipo === 'partita');
  const allenamenti = dati.voti.filter((v) => v.tipo === 'allenamento');
  const nuovo = dati.partiteSenzaVoto.length > 0;

  return (
    <div className="flex flex-col gap-8">
      {nuovo && (
        <div className="flex justify-end">
          <Nuovo giocatoreId={id!} partite={dati.partiteSenzaVoto} onSalvato={ricarica} />
        </div>
      )}
      {dati.voti.length > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-end gap-x-10 gap-y-4">
            <Media etichetta="Media voto" voti={dati.voti} grande />
            <Media etichetta="Partite" voti={partite} grande />
            <Media etichetta="Allenamenti" voti={allenamenti} grande />
          </CardContent>
        </Card>
      )}

      {dati.voti.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessun voto</EmptyTitle>
            <EmptyDescription>I voti di partita e allenamento compariranno qui.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        // Su schermi grandi due colonne, come gli attributi: le card di una riga sono alte uguali.
        <div className="grid gap-4 @4xl:grid-cols-2">
          <Sezione
            giocatoreId={id!}
            voce="partite"
            titolo="Partite"
            voti={partite}
            onCambio={ricarica}
          >
            {(v) => (
              <>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{v.avversario ?? '—'}</span>
                    <span className="text-xs text-muted-foreground">
                      {v.squadra} · {v.stagione}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{v.minuti ?? '—'}</TableCell>
              </>
            )}
          </Sezione>
          <Sezione
            giocatoreId={id!}
            voce="allenamenti"
            titolo="Allenamenti"
            voti={allenamenti}
            onCambio={ricarica}
          >
            {(v) => (
              // Su due righe come l'avversario delle partite: le righe delle due card sono alte uguali.
              <TableCell>
                <div className="flex flex-col">
                  <span>{v.squadra}</span>
                  <span className="text-xs text-muted-foreground">{v.stagione}</span>
                </div>
              </TableCell>
            )}
          </Sezione>
        </div>
      )}
    </div>
  );
}

/** Il voto nel suo badge, colorato per fascia. */
function BadgeVoto({ valore }: { valore: number }) {
  return (
    <Badge className={cn('min-w-9 tabular-nums', coloreVoto(valore))}>
      {valore.toLocaleString('it-IT')}
    </Badge>
  );
}

/** Una media del riassunto: il pallino dice la fascia, come nelle tendine. */
function Media({
  etichetta,
  voti,
  grande = false,
}: {
  etichetta: string;
  voti: VotoRiga[];
  grande?: boolean;
}) {
  const media =
    voti.length > 0
      ? Math.round((voti.reduce((t, v) => t + v.valore, 0) / voti.length) * 10) / 10
      : null;
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {etichetta}
      </span>
      <span className="flex items-center gap-2">
        {media !== null && (
          <span
            aria-hidden
            className={cn(
              'shrink-0 rounded-full',
              grande ? 'size-3' : 'size-2.5',
              coloreVoto(media),
            )}
          />
        )}
        <span className={cn('font-bold tabular-nums', grande ? 'text-4xl' : 'text-2xl')}>
          {media?.toLocaleString('it-IT') ?? '—'}
        </span>
        <span className="text-sm text-muted-foreground">
          {voti.length === 1 ? '1 voto' : `${voti.length} voti`}
        </span>
      </span>
    </div>
  );
}

/**
 * Card grigia con l'icona della voce di menu e il titolo in maiuscolo, come
 * gli attributi; le colonne proprie del tipo le passa chi la usa. Ogni card
 * ha la sua pagina nella query string.
 */
function Sezione({
  giocatoreId,
  voce: chiave,
  titolo,
  voti,
  onCambio,
  children,
}: {
  giocatoreId: string;
  voce: string;
  titolo: string;
  voti: VotoRiga[];
  onCambio: () => Promise<void>;
  children: (v: VotoRiga) => ReactNode;
}) {
  const { righe, barra, inizio } = usePaginazione(voti, `pagina-${chiave}`);
  if (voti.length === 0) return null;
  const Icona = voce(chiave).icona;
  const partita = chiave === 'partite';
  return (
    <Card ref={inizio} className="scroll-mt-(--altezza-testata) bg-muted">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <Icona aria-hidden className="size-5" />
          <h2>{titolo}</h2>
        </CardTitle>
      </CardHeader>
      {/* Il riquadro della tabella e alto quanto le sue righe, anche se la card vicina e piu lunga. */}
      <CardContent className="flex flex-col gap-4 px-2 @xl:px-(--card-spacing)">
        <div className="rounded-lg border bg-card px-3 py-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>{partita ? 'Avversario' : 'Squadra'}</TableHead>
                {partita && <TableHead className="text-right">Min</TableHead>}
                <TableHead className="text-right">Voto</TableHead>
                {/* Le azioni restano a destra mentre le altre colonne scorrono. */}
                <TableHead className="sticky right-0 bg-card">
                  <span className="sr-only">Azioni</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {righe.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{dataBreve(v.data)}</TableCell>
                  {children(v)}
                  <TableCell className="text-right">
                    <BadgeVoto valore={v.valore} />
                  </TableCell>
                  <TableCell className="sticky right-0 bg-card">
                    <div className="flex justify-end">
                      {v.modificabile && (
                        <Modifica giocatoreId={giocatoreId} voto={v} onSalvato={onCambio} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {barra}
      </CardContent>
    </Card>
  );
}

function Nuovo({
  giocatoreId,
  partite,
  onSalvato,
}: {
  giocatoreId: string;
  partite: PartitaSenzaVoto[];
  onSalvato: () => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  return (
    <>
      <PulsanteNuovo etichetta="Nuovo voto" onClick={() => setAperto(true)} />
      <FormVoto
        aperto={aperto}
        onAperto={setAperto}
        partite={partite}
        iniziale={{ partitaId: partite[0]!.id, valore: 6 }}
        onSalvato={onSalvato}
        giocatoreId={giocatoreId}
        titolo="Nuovo voto"
      />
    </>
  );
}

function Modifica({
  giocatoreId,
  voto,
  onSalvato,
}: {
  giocatoreId: string;
  voto: VotoRiga;
  onSalvato: () => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  const partite: PartitaSenzaVoto[] = voto.partitaId
    ? [
        {
          id: voto.partitaId,
          data: voto.data,
          avversario: voto.avversario ?? '—',
          squadra: voto.squadra,
          minuti: voto.minuti,
        },
      ]
    : [];
  return (
    <>
      <ComandoIcona
        etichetta="Modifica il voto"
        Icona={PencilIcon}
        size="icon-sm"
        onClick={() => setAperto(true)}
      />
      {voto.partitaId ? (
        <FormVoto
          aperto={aperto}
          onAperto={setAperto}
          partite={partite}
          iniziale={{ partitaId: voto.partitaId, valore: voto.valore }}
          onSalvato={onSalvato}
          giocatoreId={giocatoreId}
          titolo="Modifica voto"
        />
      ) : (
        <FormVoto
          aperto={aperto}
          onAperto={setAperto}
          partite={[]}
          iniziale={{ allenamentoId: voto.allenamentoId!, valore: voto.valore }}
          onSalvato={onSalvato}
          giocatoreId={giocatoreId}
          titolo="Modifica voto"
        />
      )}
    </>
  );
}

function FormVoto({
  aperto,
  onAperto,
  partite,
  iniziale,
  onSalvato,
  giocatoreId,
  titolo,
}: {
  aperto: boolean;
  onAperto: (v: boolean) => void;
  partite: PartitaSenzaVoto[];
  iniziale: { partitaId?: string; allenamentoId?: string; valore: number };
  onSalvato: () => Promise<void>;
  giocatoreId: string;
  titolo: string;
}) {
  const [partitaId, setPartitaId] = useState(iniziale.partitaId ?? '');
  const [valore, setValore] = useState(String(iniziale.valore));
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setPartitaId(iniziale.partitaId ?? '');
    setValore(String(iniziale.valore));
    setErrori({});
    setErroreServer(null);
  }, [aperto, iniziale.partitaId, iniziale.valore]);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = votoGiocatoreSchema.safeParse({
      partitaId: iniziale.allenamentoId ? undefined : partitaId || iniziale.partitaId,
      allenamentoId: iniziale.allenamentoId,
      valore: Number(valore),
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/giocatori/${giocatoreId}/voti`, { metodo: 'PUT', corpo: esito.data });
      await onSalvato();
      toast.success('Voto salvato.');
      onAperto(false);
    } catch (err) {
      setErroreServer(messaggio(err));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Dialog open={aperto} onOpenChange={onAperto}>
      <DialogContent>
        <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
          <DialogHeader>
            <DialogTitle>{titolo}</DialogTitle>
            <DialogDescription>Scala da 1 a 10 a mezzi punti.</DialogDescription>
          </DialogHeader>
          <FieldGroup>
            {partite.length > 0 && !iniziale.allenamentoId && (
              <Field>
                <FieldLabel htmlFor="voto-partita">Partita</FieldLabel>
                <Select value={partitaId} onValueChange={setPartitaId}>
                  <SelectTrigger id="voto-partita" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {partite.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {dataBreve(p.data)} · {p.avversario} ({p.squadra})
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field data-invalid={Boolean(errori.valore)}>
              <FieldLabel htmlFor="voto-valore">Voto</FieldLabel>
              <Select value={valore} onValueChange={setValore}>
                <SelectTrigger id="voto-valore" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {VALORI_SCALA.map((v) => (
                      <SelectItem key={v} value={String(v)}>
                        {v.toLocaleString('it-IT')}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldError>{errori.valore}</FieldError>
            </Field>
          </FieldGroup>
          {erroreServer && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Salvataggio non riuscito</AlertTitle>
              <AlertDescription>{erroreServer}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <PulsanteInvio type="submit" inCorso={inCorso}>
              Salva
            </PulsanteInvio>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
